import React from "react";
import styled from "styled-components";

export interface DeliveryInfo {
  recipientName: string;
  postcode: string;
  address: string;
  addressDetail: string;
  phone1: string;
  phone2: string;
  phone3: string;
}

interface DeliveryAddressProps {
  value: DeliveryInfo;
  onChange: (next: DeliveryInfo) => void;
}

declare global {
  interface Window {
    daum?: {
      Postcode: new (options: {
        oncomplete: (data: {
          zonecode: string;
          address: string;
          roadAddress: string;
          jibunAddress: string;
          buildingName?: string;
        }) => void;
      }) => { open: () => void };
    };
  }
}

const DeliveryAddress: React.FC<DeliveryAddressProps> = ({ value, onChange }) => {
  const update = (patch: Partial<DeliveryInfo>) =>
    onChange({ ...value, ...patch });

  const openPostcode = () => {
    if (!window.daum?.Postcode) {
      alert("주소 검색 서비스를 불러올 수 없습니다. 잠시 후 다시 시도해주세요.");
      return;
    }
    new window.daum.Postcode({
      oncomplete: (data) => {
        const baseAddress = data.roadAddress || data.jibunAddress || data.address;
        const withBuilding = data.buildingName
          ? `${baseAddress} (${data.buildingName})`
          : baseAddress;
        update({ postcode: data.zonecode, address: withBuilding });
      },
    }).open();
  };

  const handlePhone = (slot: 1 | 2 | 3) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/\D/g, "");
    if (slot === 1) update({ phone1: digits });
    else if (slot === 2) update({ phone2: digits });
    else update({ phone3: digits });
  };

  return (
    <Section>
      <SectionTitle>배송지</SectionTitle>
      <FormContainer>
        <ArrowIcon src="/assets/arrow-up.png" alt="Arrow Up" />
        <AddressForm onSubmit={(e) => e.preventDefault()}>
          <AddressInput
            placeholder=" 받는 이"
            value={value.recipientName}
            onChange={(e) => update({ recipientName: e.target.value })}
          />
          <AddressRow>
            <PostcodeInput
              placeholder=" 우편번호"
              value={value.postcode}
              readOnly
              onClick={openPostcode}
            />
            <PostcodeButton type="button" onClick={openPostcode}>
              주소 검색
            </PostcodeButton>
          </AddressRow>
          <AddressInput
            placeholder=" 주소"
            value={value.address}
            readOnly
            onClick={openPostcode}
          />
          <AddressInput
            placeholder=" 상세주소(동,호수)"
            value={value.addressDetail}
            onChange={(e) => update({ addressDetail: e.target.value })}
          />
          <PhoneInputContainer>
            <PhoneInput
              placeholder="010"
              maxLength={3}
              value={value.phone1}
              onChange={handlePhone(1)}
            />
            <PhoneSeparator>-</PhoneSeparator>
            <PhoneInput
              placeholder=""
              maxLength={4}
              value={value.phone2}
              onChange={handlePhone(2)}
            />
            <PhoneSeparator>-</PhoneSeparator>
            <PhoneInput
              placeholder=""
              maxLength={4}
              value={value.phone3}
              onChange={handlePhone(3)}
            />
          </PhoneInputContainer>
        </AddressForm>
      </FormContainer>
    </Section>
  );
};

const Section = styled.section`
  margin-top: 95px;
  padding: 0 20px;
  @media (max-width: 991px) {
    margin-top: 40px;
  }
`;

const SectionTitle = styled.h2`
  font-size: 45px;
  color: #000;
  font-weight: 700;
  margin-left: 10px;
  @media (max-width: 991px) {
    font-size: 40px;
  }
`;

const FormContainer = styled.div`
  position: relative;
`;

const AddressForm = styled.form`
  background-color: #fff;
  border-radius: 20px;
  margin-top: 18px;
  padding: 33px 66px;
  @media (max-width: 991px) {
    padding: 20px;
  }
`;

const ArrowIcon = styled.img`
  position: absolute;
  top: -90px;
  right: 20px;
  width: 30px;
  height: 30px;
`;

const AddressInput = styled.input`
  width: 97%;
  font-size: 35px;
  margin-bottom: 20px;
  border: none;
  border-bottom: 1px solid #ddd;
  padding: 10px 0;
  background: transparent;

  &:last-child {
    margin-bottom: 0;
  }
`;

const AddressRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
`;

const PostcodeInput = styled.input`
  flex: 1;
  font-size: 35px;
  border: none;
  border-bottom: 1px solid #ddd;
  padding: 10px 0;
  background: transparent;
  cursor: pointer;
`;

const PostcodeButton = styled.button`
  background-color: #00ab57;
  color: #fff;
  border: none;
  border-radius: 12px;
  padding: 16px 22px;
  font-size: 24px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background-color: #009149;
  }
`;

const PhoneInputContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const PhoneInput = styled.input`
  width: 30%;
  font-size: 35px;
  border: none;
  border-bottom: 1px solid #ddd;
  padding: 10px 0;
  text-align: center;
  margin-bottom: 20px;
  background: transparent;

  &:nth-child(1) {
    width: 20%;
  }

  &:nth-child(3) {
    width: 35%;
  }
`;

const PhoneSeparator = styled.span`
  font-size: 35px;
  padding: 0 5px;
`;

export default DeliveryAddress;
